import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../config/api';
import './ProductCatalog.css';

const ProductCatalog = ({ onAddToCart, onUpdateQuantity, onRemoveFromCart, cartItems }) => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.menuItems);
      const data = await response.json();

      // Flatten categories and items
      const allProducts = [];
      const categoryNames = new Set();

      data.forEach(category => {
        categoryNames.add(category.name);
        category.items.forEach(item => {
          allProducts.push({
            ...item,
            category: category.name
          });
        });
      });

      setProducts(allProducts);
      setCategories(['all', ...Array.from(categoryNames)]);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch products:', error);
      setLoading(false);
    }
  };

  const filteredProducts = selectedCategory === 'all'
    ? products
    : products.filter(p => p.category === selectedCategory);

  const getItemQuantityInCart = (productId) => {
    const item = cartItems.find(i => i.id === productId);
    return item ? item.quantity : 0;
  };

  const getProductEmoji = (product) => {
    const name = product.name.toLowerCase();
    const category = product.category ? product.category.toLowerCase() : '';

    // Drinks
    if (name.includes('juice') || name.includes('orange')) return '🍊';
    if (name.includes('lemon') || name.includes('mint')) return '🍋';
    if (name.includes('ayran') || name.includes('yogurt')) return '🥛';
    if (name.includes('water')) return '💧';
    if (name.includes('coffee')) return '☕';
    if (name.includes('tea')) return '🍵';
    if (category.includes('drink') || category.includes('beverage')) return '🥤';

    // Desserts
    if (name.includes('baklava')) return '🥮';
    if (name.includes('kunafa') || name.includes('knafeh')) return '🍰';
    if (name.includes('umm ali')) return '🍮';
    if (category.includes('dessert') || category.includes('sweet')) return '🧁';

    // Appetizers & Sides
    if (name.includes('hummus')) return '🫘';
    if (name.includes('falafel')) return '🧆';
    if (name.includes('baba ghanoush') || name.includes('baba ganoush')) return '🍆';
    if (name.includes('salad') || name.includes('tabbouleh')) return '🥗';
    if (name.includes('fries') || name.includes('french fries')) return '🍟';
    if (name.includes('rice')) return '🍚';

    // Shawarma - Chicken
    if (name.includes('chicken')) {
      if (name.includes('wrap') || name.includes('sandwich')) return '🌯';
      if (name.includes('plate') || name.includes('platter')) return '🍗';
      return '🍗';
    }

    // Shawarma - Beef/Meat
    if (name.includes('beef') || name.includes('meat') || name.includes('lamb')) {
      if (name.includes('wrap') || name.includes('sandwich')) return '🌯';
      if (name.includes('plate') || name.includes('platter')) return '🥩';
      return '🥩';
    }

    // Mixed Shawarma
    if (name.includes('mixed') || name.includes('combo')) {
      if (name.includes('wrap')) return '🌯';
      return '🍽️';
    }

    // Default - check by category
    if (category.includes('shawarma')) return '🌯';
    if (category.includes('appetizer')) return '🥙';
    if (category.includes('main') || category.includes('entree')) return '🍽️';

    // Ultimate fallback
    return '🌯';
  };

  if (loading) {
    return <div className="loading">Loading menu...</div>;
  }

  return (
    <div className="product-catalog">
      <div className="catalog-header">
        <h2>Our Menu</h2>
        <p className="subtitle">Choose from our delicious Middle Eastern cuisine</p>
      </div>

      {/* Category Filters */}
      <div className="category-filters">
        {categories.map(category => (
          <button
            key={category}
            className={`category-button ${selectedCategory === category ? 'active' : ''}`}
            onClick={() => setSelectedCategory(category)}
          >
            {category === 'all' ? 'All Items' : category}
          </button>
        ))}
      </div>

      {/* Product Grid */}
      <div className="product-grid">
        {filteredProducts.map(product => {
          const quantityInCart = getItemQuantityInCart(product.id);

          return (
            <div key={product.id} className="product-card">
              <div
                className={`product-image ${product.image_url ? 'has-image' : ''}`}
                data-emoji={getProductEmoji(product)}
                style={product.image_url ? { backgroundImage: `url(${product.image_url})` } : {}}
              >
                {product.is_vegetarian === 1 && (
                  <span className="badge veg">🌱 Vegetarian</span>
                )}
                {product.is_spicy === 1 && (
                  <span className="badge spicy">🌶️ Spicy</span>
                )}
              </div>

              <div className="product-info">
                <h3 className="product-name">{product.name}</h3>
                <p className="product-description">
                  {product.description || 'Delicious Middle Eastern dish'}
                </p>

                <div className="product-footer">
                  <span className="product-price">AED {product.price.toFixed(2)}</span>

                  {quantityInCart > 0 ? (
                    <div className="quantity-control">
                      <button
                        className="qty-btn minus"
                        onClick={() => onUpdateQuantity(product.id, quantityInCart - 1)}
                      >−</button>
                      <span className="qty-value">{quantityInCart}</span>
                      <button
                        className="qty-btn plus"
                        onClick={() => onAddToCart(product)}
                      >+</button>
                    </div>
                  ) : (
                    <button
                      className="add-to-cart-btn"
                      onClick={() => onAddToCart(product)}
                    >
                      + Add to Cart
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredProducts.length === 0 && (
        <div className="no-products">
          <p>No items found in this category</p>
        </div>
      )}
    </div>
  );
};

export default ProductCatalog;
